/**
 * Catalog upgrade — bring an existing plant's equipment up to the current catalog.
 *
 * Every entity stores a frozen copy of its template's definition (`data.entity`), so a
 * parameter the catalog gains reaches NEW equipment only. An imported value lands on an
 * equipment's standard slot only when that slot is in the stored definition, so a plant
 * provisioned before the catalog grew needs this pass before its next import.
 *
 * The rules, all enforced here and pinned by catalogUpgrade.test.ts:
 *   - append-only: a missing catalog parameter is appended; nothing is renamed, removed
 *     or reordered, and no stored field is overwritten;
 *   - absent semantics (`paramClass`, `setpointOf`: contract K2) are filled on existing
 *     parameters; a present one is never changed;
 *   - a catalog `value` is a demo datum, so it (and its provenance) never enters a live
 *     plant;
 *   - unit and data-kind differences are reported, never applied: changing either would
 *     change what a stored reading means;
 *   - every other key of the stored row survives, because the upsert replaces `data` whole.
 */
import type { EntityDef, ParameterDef } from "@foundry/foundry-plant";
import type { Paginated, TwinEntity } from "@/lib/api/types";
import type { TwinEntityCreate } from "@/lib/api/twin-entities";
import { CATALOG_BY_ID } from "./catalog/entityCatalog";

/** Semantic fields a stored parameter may lack; filled from the catalog when absent. */
const SEMANTIC_FIELDS = ["paramClass", "setpointOf"] as const satisfies readonly (keyof ParameterDef)[];
type SemanticField = (typeof SEMANTIC_FIELDS)[number];

export interface ParamDifference {
  name: string;
  field: "unit" | "dataKind";
  stored: string | undefined;
  catalog: string | undefined;
}

export interface EntityUpgradePlan {
  /** Catalog parameters the stored definition lacks, in catalog order, ready to append. */
  add: ParameterDef[];
  /** Existing parameters whose absent semantic fields the catalog fills. */
  semantics: { name: string; fields: Partial<Pick<ParameterDef, SemanticField>> }[];
  /** Where the stored definition and the catalog disagree. Reported only. */
  differences: ParamDifference[];
}

/** A catalog parameter as a live plant receives it: everything but the demo value. */
function forLivePlant(p: ParameterDef): ParameterDef {
  const out = structuredClone(p);
  delete out.value;
  delete out.valueProvenance;
  return out;
}

export function planEntityUpgrade(stored: EntityDef, catalog: EntityDef): EntityUpgradePlan {
  const have = new Map((stored.parameters ?? []).map((p) => [p.name, p]));
  const plan: EntityUpgradePlan = { add: [], semantics: [], differences: [] };
  for (const c of catalog.parameters) {
    const s = have.get(c.name);
    if (!s) {
      plan.add.push(forLivePlant(c));
      continue;
    }
    const fields: Partial<Pick<ParameterDef, SemanticField>> = {};
    for (const f of SEMANTIC_FIELDS) {
      if (s[f] === undefined && c[f] !== undefined) Object.assign(fields, { [f]: c[f] });
    }
    if (Object.keys(fields).length) plan.semantics.push({ name: c.name, fields });
    for (const f of ["unit", "dataKind"] as const) {
      if (s[f] !== undefined && c[f] !== undefined && s[f] !== c[f]) {
        plan.differences.push({ name: c.name, field: f, stored: s[f], catalog: c[f] });
      }
    }
  }
  return plan;
}

/** The stored definition with the plan applied. Never mutates its input. */
export function applyEntityUpgrade<T extends EntityDef>(stored: T, plan: EntityUpgradePlan): T {
  const fill = new Map(plan.semantics.map((s) => [s.name, s.fields]));
  const existing = (stored.parameters ?? []).map((p) => (fill.has(p.name) ? { ...p, ...fill.get(p.name) } : { ...p }));
  const names = new Set(existing.map((p) => p.name));
  const appended = plan.add.filter((p) => !names.has(p.name)).map((p) => structuredClone(p));
  return { ...stored, parameters: [...existing, ...appended] };
}

export type RowUpgrade =
  | {
      kind: "upgrade";
      id: string;
      name: string;
      templateId: string;
      plan: EntityUpgradePlan;
      body: TwinEntityCreate;
    }
  | { kind: "current"; id: string; name: string; templateId: string; plan: EntityUpgradePlan }
  | { kind: "skipped"; id: string; name: string; templateId: string; reason: string };

function isRecord(v: unknown): v is Record<string, unknown> {
  return Boolean(v) && typeof v === "object" && !Array.isArray(v);
}

/** One stored entity row → what an upgrade would write, or why it is left alone. */
export function planRowUpgrade(row: TwinEntity, catalogById: Record<string, EntityDef> = CATALOG_BY_ID): RowUpgrade {
  const base = { id: row.id, name: row.name, templateId: row.template_id };
  const catalog = row.template_id ? catalogById[row.template_id] : undefined;
  if (!catalog) return { ...base, kind: "skipped", reason: "its template is not in the catalog" };
  const data = isRecord(row.data) ? row.data : {};
  const stored = data.entity;
  if (!isRecord(stored)) return { ...base, kind: "skipped", reason: "no stored definition" };
  const entity = stored as unknown as EntityDef;
  if (entity.templateId && entity.templateId !== row.template_id) {
    return { ...base, kind: "skipped", reason: "the row and its stored definition name different templates" };
  }
  const plan = planEntityUpgrade(entity, catalog);
  if (!plan.add.length && !plan.semantics.length) return { ...base, kind: "current", plan };
  return {
    ...base,
    kind: "upgrade",
    plan,
    body: {
      id: row.id,
      kind: row.kind,
      name: row.name,
      template_id: row.template_id,
      data: { ...data, entity: applyEntityUpgrade(entity, plan) },
    },
  };
}

export interface UpgradeSummary {
  entities: number;
  toUpgrade: number;
  /** Parameters appended across all entities. */
  parameters: number;
  /** Existing parameters whose semantics are filled. */
  semantics: number;
  current: number;
  skipped: number;
}

export function summarise(rows: RowUpgrade[]): UpgradeSummary {
  const s: UpgradeSummary = { entities: rows.length, toUpgrade: 0, parameters: 0, semantics: 0, current: 0, skipped: 0 };
  for (const r of rows) {
    if (r.kind === "upgrade") {
      s.toUpgrade += 1;
      s.parameters += r.plan.add.length;
      s.semantics += r.plan.semantics.length;
    } else if (r.kind === "current") {
      s.current += 1;
    } else {
      s.skipped += 1;
    }
  }
  return s;
}

/** Every entity of the active org. The server caps a page at 200 and reports the page's own
 *  length as `total`, so paging stops at the first short page. */
export async function listAllEntities(
  list: (p: { limit: number; offset: number }) => Promise<Paginated<TwinEntity>>,
  pageSize = 200,
): Promise<TwinEntity[]> {
  const out: TwinEntity[] = [];
  for (let offset = 0; ; offset += pageSize) {
    const page = await list({ limit: pageSize, offset });
    out.push(...page.items);
    if (page.items.length < pageSize) return out;
  }
}

export interface ApplyDeps {
  get: (id: string) => Promise<TwinEntity>;
  create: (body: TwinEntityCreate) => Promise<TwinEntity>;
  catalogById?: Record<string, EntityDef>;
}

export interface ApplyResult {
  id: string;
  name: string;
  status: "written" | "already current" | "skipped" | "failed";
  added?: number;
  error?: string;
}

/** Write the planned upgrades one entity at a time. Each row is re-read just before its
 *  write and re-planned from that copy, so an edit made since the preview is kept and a
 *  row someone else already upgraded is left alone. A failure is recorded and the next
 *  row still runs. */
export async function applyUpgrades(plans: RowUpgrade[], deps: ApplyDeps): Promise<ApplyResult[]> {
  const out: ApplyResult[] = [];
  for (const p of plans) {
    if (p.kind !== "upgrade") continue;
    try {
      const fresh = planRowUpgrade(await deps.get(p.id), deps.catalogById);
      if (fresh.kind === "current") {
        out.push({ id: p.id, name: p.name, status: "already current" });
      } else if (fresh.kind === "skipped") {
        out.push({ id: p.id, name: p.name, status: "skipped", error: fresh.reason });
      } else {
        await deps.create(fresh.body);
        out.push({ id: p.id, name: p.name, status: "written", added: fresh.plan.add.length });
      }
    } catch (err) {
      out.push({ id: p.id, name: p.name, status: "failed", error: err instanceof Error ? err.message : String(err) });
    }
  }
  return out;
}
